import { MODULES } from '@epc/contracts';
import { Link, useRouter } from '@tanstack/react-router';
import clsx from 'clsx';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { createContext, type ReactNode, useContext, useEffect, useRef, useState } from 'react';
import { ApiError } from '../lib/api';

/** Звено строки «где вы находитесь»: раздел → проект → документ */
export interface Crumb {
  label: string;
  to?: string;
  /** Полное название, если в строке — короткое (шифр проекта, номер документа) */
  title?: string;
}

const CrumbsContext = createContext<((tail: Crumb[] | null) => void) | null>(null);
export const CrumbsProvider = CrumbsContext.Provider;

/** Звено вместо документа, который не открылся: нет доступа, не найден, ошибка загрузки */
function errorCrumb(error: unknown): Crumb[] {
  const status = error instanceof ApiError ? error.status : 0;
  return [{ label: status === 403 ? 'Нет доступа' : status === 404 ? 'Не найдено' : 'Ошибка загрузки' }];
}

/**
 * Страница сообщает оболочке, где человек находится после раздела: проект, заявка, договор.
 * Пока данные грузятся, передаётся null — оболочка покажет раздел и многоточие; если загрузка не
 * удалась, вторым аргументом передаётся ошибка — звено скажет, что случилось.
 */
export function useCrumbs(tail: Crumb[] | null | undefined, error?: unknown) {
  const set = useContext(CrumbsContext);
  const shown = tail === null && error ? errorCrumb(error) : tail;
  const key = shown === undefined ? 'skip' : shown ? JSON.stringify(shown) : '';
  // biome-ignore lint/correctness/useExhaustiveDependencies: звенья сравниваются по содержимому (key), а не по ссылке на массив
  useEffect(() => {
    // undefined — страница звенья не задаёт (их задаёт вложенная страница, например «не найдено»)
    if (!set || shown === undefined) return;
    set(shown);
    return () => set(null);
  }, [set, key]);
}

/** Разделы без своего пункта в меню и подстраницы с постоянным названием */
const SECTION_OVERRIDES: Array<{ prefix: string; label: string; to: string }> = [
  { prefix: '/projects/', label: 'Портфель', to: '/' },
  { prefix: '/profile', label: 'Профиль', to: '/profile' },
  { prefix: '/cost-articles', label: 'Статьи расходов', to: '/cost-articles' },
  { prefix: '/platform', label: 'Кабинет портала', to: '/platform' },
  { prefix: '/fleet', label: 'Техника', to: '/fleet' },
  { prefix: '/suppliers/cabinet', label: 'Кабинет поставщика', to: '/suppliers/cabinet' },
  { prefix: '/start', label: 'С чего начать', to: '/start' },
  { prefix: '/devboard', label: 'Доска задач разработки', to: '/devboard' },
];

const STATIC_TAILS: Record<string, string> = {
  '/site/extra-works': 'Акты допработ',
  '/site/extra-works/new': 'Новый акт допработ',
  '/site/photos': 'Фото площадки',
  '/admin/members': 'Сотрудники',
  '/admin/delegations': 'Замещения',
  '/admin/roles': 'Роли и права',
  '/admin/legal-entities': 'Юрлица группы',
  '/admin/meeting-rooms': 'Переговорные',
  '/admin/org': 'Орг-структура',
  '/admin/audit': 'Журнал изменений',
  '/admin/settings': 'Настройки',
  '/admin/license': 'Лицензия',
  '/admin/backups': 'Резервные копии',
  '/admin/branding': 'Оформление и форматы',
  '/admin/privacy': 'Данные людей',
  '/admin/files': 'Антивирус и карантин',
  '/admin/integrations': 'Обмен с внешними системами',
  '/admin/status': 'Состояние системы',
  '/platform/plans': 'Тарифы',
  '/platform/requests': 'Заявки на доступ',
  '/platform/invoices': 'Счета',
  '/platform/markets': 'Страны и рынки',
  '/platform/sales': 'Продажи',
  '/platform/events': 'Журнал',
  '/platform/pilot': 'Pilot',
  '/platform/users': 'Люди',
  '/platform/security': 'Безопасность',
  '/platform/announcements': 'Объявления',
  '/platform/team': 'Команда портала',
  '/platform/versions': 'Версии портала',
  '/roadmap/map': 'Карта связей',
};

export function sectionOf(pathname: string): Crumb & { to: string } {
  const o = SECTION_OVERRIDES.find((x) => pathname === x.prefix || pathname.startsWith(x.prefix));
  if (o) return { label: o.label, to: o.to };
  const m = MODULES.filter((x) => x.path !== '/').find(
    (x) => pathname === x.path || pathname.startsWith(`${x.path}/`),
  );
  return m ? { label: m.title, to: m.path } : { label: 'Портфель', to: '/' };
}

/** Полная строка «где вы находитесь»: раздел и то, что сообщила страница */
export function buildTrail(pathname: string, tail: Crumb[] | null): Crumb[] {
  const section = sectionOf(pathname);
  // Корень раздела: вкладка (реестры, заявки на оплату) — следующим звеном
  if (pathname === section.to) return tail?.length ? [section, ...tail] : [{ label: section.label }];
  const fixed = STATIC_TAILS[pathname];
  const rest = tail ?? (fixed ? [{ label: fixed }] : [{ label: '…' }]);
  return [section, ...rest];
}

/* ───────────────────────── Назад и вперёд ───────────────────────── */

const MAX_KEY = 'epc:nav-max';

function readMax(): number {
  try {
    return Number(sessionStorage.getItem(MAX_KEY) ?? 0) || 0;
  } catch {
    return 0;
  }
}

function writeMax(n: number) {
  try {
    sessionStorage.setItem(MAX_KEY, String(n));
  } catch {
    // Хранилище закрыто (приватное окно): «вперёд» просто не узнает о страницах после перезагрузки
  }
}

/**
 * Можно ли вернуться назад и пойти вперёд внутри портала. История браузера не говорит, есть ли
 * страницы впереди, поэтому портал помнит самый дальний шаг вкладки: переход по ссылке обрезает
 * всё, что было впереди, «назад» и «вперёд» — нет.
 */
let pageLoadSeen = false;

/** Вкладка открыта новым переходом (ссылка, адрес): всё, что было впереди в истории, браузер сбросил */
function freshLoad(): boolean {
  if (pageLoadSeen) return false;
  pageLoadSeen = true;
  try {
    const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
    return nav?.type === 'navigate';
  } catch {
    return false;
  }
}

function useHistorySteps() {
  const router = useRouter();
  const index = () => (router.history.location.state as { __TSR_index?: number }).__TSR_index ?? 0;
  const [state, setState] = useState(() => {
    const i = index();
    // Перезагрузка и «назад» браузера сохраняют страницы впереди, новый переход — нет
    const max = freshLoad() ? i : Math.max(readMax(), i);
    writeMax(max);
    return { index: i, max };
  });
  useEffect(
    () =>
      router.history.subscribe(
        ({
          location,
          action,
        }: {
          location: { state: { __TSR_index?: number } };
          action: { type: string };
        }) => {
          const i = location.state.__TSR_index ?? 0;
          setState((s) => {
            const max = action.type === 'PUSH' ? i : Math.max(s.max, i);
            writeMax(max);
            return { index: i, max };
          });
        },
      ),
    [router],
  );
  return { canBack: state.index > 0, canForward: state.index < state.max };
}

function StepButton({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className="flex size-11 shrink-0 items-center justify-center rounded-md text-ink hover:bg-tile disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent md:size-9"
    >
      {children}
    </button>
  );
}

/**
 * Кнопки «назад», «вперёд» и строка «где вы находитесь». Если вкладка открыта сразу на документе,
 * «назад» ведёт на уровень выше — в проект или раздел, а не прочь из портала.
 */
export function LocationBar({ trail, className }: { trail: Crumb[]; className?: string }) {
  const router = useRouter();
  const steps = useHistorySteps();
  const list = useRef<HTMLOListElement>(null);
  const up = [...trail.slice(0, -1)].reverse().find((c) => c.to);
  const key = trail.map((c) => c.label).join('/');
  // На узком экране строка прокручивается к текущей странице
  // biome-ignore lint/correctness/useExhaustiveDependencies: прокрутка — при смене строки, её состав в key
  useEffect(() => {
    const el = list.current;
    if (el) el.scrollLeft = el.scrollWidth;
  }, [key]);
  return (
    <nav aria-label="Где вы находитесь" className={clsx('min-w-0 items-center gap-0.5', className)}>
      <StepButton
        label={steps.canBack ? 'Назад' : up ? `Назад: ${up.label}` : 'Назад'}
        disabled={!steps.canBack && !up}
        onClick={() => {
          if (steps.canBack) router.history.back();
          else if (up?.to) void router.navigate({ to: up.to });
        }}
      >
        <ChevronLeft className="size-5" />
      </StepButton>
      <StepButton label="Вперёд" disabled={!steps.canForward} onClick={() => router.history.forward()}>
        <ChevronRight className="size-5" />
      </StepButton>
      <ol
        ref={list}
        className="ml-1.5 flex min-w-0 items-center gap-1.5 overflow-x-auto text-[14px] whitespace-nowrap [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {trail.map((c, i) => {
          const last = i === trail.length - 1;
          return (
            // biome-ignore lint/suspicious/noArrayIndexKey: звенья строки идут в постоянном порядке
            <li key={i} className="flex min-w-0 shrink-0 items-center gap-1.5">
              {i > 0 ? (
                <span aria-hidden className="text-ink-3">
                  /
                </span>
              ) : null}
              {c.to && !last ? (
                <Link
                  to={c.to}
                  title={c.title ?? c.label}
                  className="inline-flex h-11 max-w-[14ch] items-center text-ink-2 underline-offset-4 hover:text-ink hover:underline md:h-8 md:max-w-[24ch] [&>*]:truncate"
                >
                  <span className="truncate">{c.label}</span>
                </Link>
              ) : (
                <span
                  aria-current={last ? 'page' : undefined}
                  title={c.title ?? c.label}
                  className={clsx(
                    'truncate',
                    // Текущая страница видна целиком: строка прокручена к ней, длинное — до 70 % ширины
                    last
                      ? 'max-w-[70vw] font-semibold text-ink md:max-w-[48ch]'
                      : 'max-w-[14ch] text-ink-2 md:max-w-[24ch]',
                  )}
                >
                  {c.label}
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
